// @flow
// import-sort-ignore

import React from 'react';
import { connect } from 'react-redux';
import { find, flowRight, get, isFunction } from 'lodash';

import { ChangePlan } from '@zapier/billing';
import { addParamsToUrl } from '@zapier/url-utils';
import { toPlan } from '@zapier/billing/src/utils'; // eslint-disable-line @zapier/zapier/disallow-incorrect-import-notation
import { usePaymentSource } from '@zapier/payment';

import * as Account from 'app/entities/Account';
import * as Plan from 'app/entities/Plan';
import BillingActions from 'app/billing/BillingActions';
import ChangePlanCompleteModalContainer from 'app/billing/components/ChangePlanCompleteModalContainer';
import LoadingBlockOrComponent from 'app/common/components/LoadingBlockOrComponent';
import MaybeRedirectOnMount from 'app/common/components/MaybeRedirectOnMount';
import PaymentApp from 'app/payment/components/App';
import PreloadPage from 'app/common/components/PreloadPage';
import hocify from 'app/common/components/hocify';
import { currentUrl, getQueryParam } from 'app/common/utils/RouterSelectors';
import {
  getBillingInterval,
  getChangeLabel,
  getChangeLabelForPlanName,
  getEnticements,
} from 'app/billing/utils';
import {
  selectIsConfirmingPlanChange,
  selectIsInFlight,
  selectPlanByName,
  selectSelectedBillingIntervals,
} from 'app/billing/Selectors';

import type {
  ChangeType,
  Interval,
  Plan as PlanType,
} from '@zapier/common-utils';

import type { Dispatch } from 'app/common/types';

type Props = {
  accountId: number,
  accountName?: string,
  ChangePlanCompleteModal: ComponentType<*>,
  changeType: ChangeType,
  companyName?: string,
  enticements: { month: string, year: string },
  error: ?string,
  fromPlanName: string,
  heading: string,
  isBillingInFlight: boolean,
  isCompact: boolean,
  isConfirmingPlanChange: boolean,
  isCreatingNewAccount?: boolean,
  isFree: boolean,
  isNewAccount?: boolean,
  onCancelChangePlan: () => void,
  onChangePlan: () => void,
  onSelectBillingInterval: (planName: string, interval: Interval) => void,
  plan: PlanType,
  selectedBillingInterval: string,
  subheading?: string,
  submitText: string,
};

const mapStateToProps = (state, props) => {
  const { fromPlanName, toPlanName, changeType } = props.params;

  const planName = toPlanName || fromPlanName; // if changeType is 'change', there is no toPlanName

  const currentAccount = Account.selectors.all.currentAccount(state);
  const accountId = Account.selectors.all.currentAccountId(state);
  const accountName =
    Account.selectors.ownerEmail(currentAccount) ||
    Account.selectors.name(currentAccount);
  const companyName = Account.selectors.companyName(currentAccount);
  const isBillingInFlight = selectIsInFlight(state);

  const error =
    Account.selectors.isFailure(currentAccount) &&
    Account.selectors.statusMessage(currentAccount);

  const isConfirmingPlanChange = selectIsConfirmingPlanChange(state);
  const accountPlan = selectPlanByName(state, { planName });
  const selectedBillingInterval =
    getQueryParam(state, 'interval') ||
    getBillingInterval(selectSelectedBillingIntervals(state), accountPlan);
  const plan = toPlan(accountPlan, selectedBillingInterval);
  const isFree = plan.amount === 0;
  const isLoading =
    Plan.selectors.all.isLoadImpending(state) || !currentAccount;
  const hasNoPlan = !isLoading && !accountPlan;
  const interval = find(get(plan, 'intervals', []), anInterval => {
    return get(anInterval, 'interval') === selectedBillingInterval;
  });
  const hasNoValidInterval = !isLoading && !interval;

  const changeLabel = getChangeLabel(changeType);
  const heading = getChangeLabelForPlanName(changeType, plan.name);
  const subheading = `${changeLabel} account`;
  const submitText =
    changeType === 'change'
      ? `${changeLabel} Account Billing`
      : `${changeLabel} My Account`;

  const enticements = !accountPlan ? {} : getEnticements(accountPlan);

  return {
    accountId,
    accountName,
    ChangePlanCompleteModal: ChangePlanCompleteModalContainer,
    changeType,
    companyName,
    enticements,
    error,
    fromPlanName,
    heading,
    isBillingInFlight,
    isConfirmingPlanChange,
    isLoading,
    isFree,
    plan,
    redirectUrl: hasNoPlan
      ? `/app/billing/plans`
      : addParamsToUrl(currentUrl(state), {
          interval: get(plan, ['intervals', '0', 'interval']),
        }),
    selectedBillingInterval,
    shouldRedirect: hasNoPlan || hasNoValidInterval,
    subheading,
    submitText,
  };
};

const mapDispatchToProps = (dispatch: Dispatch, props) => {
  return {
    onLoad() {
      dispatch(Plan.actions.loadCollection());
    },
    onSelectBillingInterval(planName, interval) {
      dispatch(BillingActions.selectBillingInterval(planName, interval));
    },
    async onChangePlan(fromPlanName, toPlanName, interval, changeType) {
      if (changeType === 'downgrade') {
        dispatch(
          BillingActions.confirmDowngradePlan(
            fromPlanName,
            toPlanName,
            interval
          )
        );
      } else {
        await dispatch(
          BillingActions.upgradePlan(toPlanName, interval, {
            shouldNotLoadZaps: props.shouldNotLoadZaps,
          })
        );

        if (isFunction(props.onFinishUpgradePlan)) {
          props.onFinishUpgradePlan();
        } else {
          dispatch(BillingActions.confirmPlanChange());
        }
      }
    },
    onCancelChangePlan(fromPlanName, toPlanName, interval, changeType) {
      if (isFunction(props.onCancelChangePlan)) {
        props.onCancelChangePlan();
      } else {
        dispatch(
          BillingActions.cancelChangePlan(
            fromPlanName,
            toPlanName,
            interval,
            changeType
          )
        );
      }
    },
  };
};

const ChangePlanContainer = (props: Props) => {
  const { forceSourceEdit, primarySource } = usePaymentSource({
    accountId: props.accountId,
  });

  const isSubmitDisabled = !props.isFree && (!primarySource || forceSourceEdit);

  return (
    <ChangePlan
      {...props}
      isSubmitDisabled={isSubmitDisabled}
      PaymentApp={props.isFree ? null : PaymentApp}
    />
  );
};

export default flowRight(
  connect(mapStateToProps, mapDispatchToProps),
  hocify(LoadingBlockOrComponent, { LoadingComponent: PreloadPage }),
  hocify(MaybeRedirectOnMount)
)(ChangePlanContainer);
